import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Linkedin, Award, Code, Sparkles, ExternalLink } from 'lucide-react';

function DeveloperPage() {
  const { theme, currentThemeName } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: currentThemeName === 'dark'
        ? '#0f172a'
        : currentThemeName === 'beige'
        ? '#f5f5dc'
        : '#ffffff',
      color: theme.text,
      padding: '20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background gradient */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '400px',
        background: currentThemeName === 'dark'
          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%)'
          : currentThemeName === 'beige'
          ? 'linear-gradient(135deg, rgba(139, 69, 19, 0.08) 0%, rgba(160, 82, 45, 0.08) 100%)'
          : 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(147, 51, 234, 0.08) 100%)',
        zIndex: 0
      }} />

      <div style={{ 
        maxWidth: 1000, 
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: currentThemeName === 'dark'
              ? 'rgba(30, 41, 59, 0.8)'
              : currentThemeName === 'beige'
              ? 'rgba(245, 245, 220, 0.9)'
              : 'rgba(255, 255, 255, 0.95)',
            border: currentThemeName === 'dark'
              ? '1px solid rgba(59, 130, 246, 0.2)'
              : currentThemeName === 'beige'
              ? '1px solid rgba(139, 69, 19, 0.15)'
              : '1px solid rgba(59, 130, 246, 0.1)',
            borderRadius: '12px',
            color: theme.primaryText,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginBottom: '24px',
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = currentThemeName === 'dark'
              ? 'rgba(30, 41, 59, 1)'
              : currentThemeName === 'beige'
              ? 'rgba(245, 245, 220, 1)'
              : 'rgba(255, 255, 255, 1)';
            e.currentTarget.style.transform = 'translateX(-4px)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = currentThemeName === 'dark'
              ? 'rgba(30, 41, 59, 0.8)'
              : currentThemeName === 'beige'
              ? 'rgba(245, 245, 220, 0.9)'
              : 'rgba(255, 255, 255, 0.95)';
            e.currentTarget.style.transform = 'translateX(0)';
          }}
        >
          <ArrowLeft size={16} />
          {t('developer.back')}
        </button>

        {/* Main Card */}
        <div style={{
          background: currentThemeName === 'dark' 
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
            : currentThemeName === 'beige'
            ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.9) 0%, rgba(250, 235, 215, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%)',
          border: currentThemeName === 'dark'
            ? '1px solid rgba(59, 130, 246, 0.2)'
            : currentThemeName === 'beige'
            ? '1px solid rgba(139, 69, 19, 0.15)'
            : '1px solid rgba(59, 130, 246, 0.1)',
          borderRadius: '24px',
          padding: '48px',
          boxShadow: currentThemeName === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
            : currentThemeName === 'beige'
            ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
            : '0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Profile Section */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '40px',
            textAlign: 'center'
          }}>
            {/* Photo */}
            <div style={{
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: currentThemeName === 'dark'
                ? '4px solid rgba(59, 130, 246, 0.3)'
                : currentThemeName === 'beige'
                ? '4px solid rgba(139, 69, 19, 0.2)'
                : '4px solid rgba(59, 130, 246, 0.2)',
              marginBottom: '24px',
              boxShadow: currentThemeName === 'dark'
                ? '0 8px 32px rgba(59, 130, 246, 0.2)'
                : '0 8px 32px rgba(0, 0, 0, 0.1)',
              position: 'relative'
            }}>
              <img 
                src="/Ali_Can_Acar.png" 
                alt="Ali Can Acar"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  // Fallback if image doesn't load
                  console.error('Failed to load image:', e.target.src);
                }}
              />
            </div>

            {/* Name */}
            <h1 style={{
              fontSize: '36px',
              fontWeight: '700',
              marginBottom: '12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
              Ali Can Acar
            </h1>

            {/* Title */}
            <p style={{
              fontSize: '20px',
              fontWeight: '500',
              color: currentThemeName === 'dark' ? '#cbd5e1' : theme.textSecondary,
              marginBottom: '8px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
              {t('developer.fullStackDeveloper')}
            </p>

            {/* Experience */}
            <p style={{
              fontSize: '16px',
              color: currentThemeName === 'dark' ? '#94a3b8' : theme.textTertiary,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
              {t('developer.yearsExperience')}
            </p>
          </div>

          {/* Vision Statement */}
          <div style={{
            marginBottom: '32px',
            padding: '24px',
            background: currentThemeName === 'dark'
              ? 'rgba(59, 130, 246, 0.1)'
              : currentThemeName === 'beige'
              ? 'rgba(139, 69, 19, 0.08)'
              : 'rgba(59, 130, 246, 0.05)',
            borderRadius: '16px',
            border: currentThemeName === 'dark'
              ? '1px solid rgba(59, 130, 246, 0.2)'
              : currentThemeName === 'beige'
              ? '1px solid rgba(139, 69, 19, 0.15)'
              : '1px solid rgba(59, 130, 246, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <Sparkles size={24} color={theme.primary || '#3b82f6'} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: theme.primaryText,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}>
                {t('developer.vision')}
              </h2>
            </div>
            <p style={{
              fontSize: '16px',
              lineHeight: '1.6',
              color: currentThemeName === 'dark' ? '#e2e8f0' : theme.text,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
              {t('developer.visionStatement')}
            </p>
          </div>

          {/* Achievements */}
          <div style={{
            marginBottom: '32px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <Award size={24} color={theme.primary || '#3b82f6'} />
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: theme.primaryText,
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}>
                {t('developer.achievements')}
              </h2>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                padding: '20px',
                background: currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.5)'
                  : currentThemeName === 'beige'
                  ? 'rgba(250, 235, 215, 0.5)'
                  : 'rgba(248, 250, 252, 0.8)',
                borderRadius: '12px',
                border: currentThemeName === 'dark'
                  ? '1px solid rgba(59, 130, 246, 0.15)'
                  : currentThemeName === 'beige'
                  ? '1px solid rgba(139, 69, 19, 0.1)'
                  : '1px solid rgba(59, 130, 246, 0.08)',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: theme.primary || '#3b82f6',
                  marginBottom: '8px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  8
                </div>
                <div style={{
                  fontSize: '14px',
                  color: currentThemeName === 'dark' ? '#cbd5e1' : theme.textSecondary,
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  {t('developer.hackathonWins')}
                </div>
              </div>
              <div style={{
                padding: '20px',
                background: currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.5)'
                  : currentThemeName === 'beige'
                  ? 'rgba(250, 235, 215, 0.5)'
                  : 'rgba(248, 250, 252, 0.8)',
                borderRadius: '12px',
                border: currentThemeName === 'dark'
                  ? '1px solid rgba(59, 130, 246, 0.15)'
                  : currentThemeName === 'beige'
                  ? '1px solid rgba(139, 69, 19, 0.1)'
                  : '1px solid rgba(59, 130, 246, 0.08)',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: theme.primary || '#3b82f6',
                  marginBottom: '8px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  20+
                </div>
                <div style={{
                  fontSize: '14px',
                  color: currentThemeName === 'dark' ? '#cbd5e1' : theme.textSecondary,
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  {t('developer.yearsExperience')}
                </div>
              </div>
              <div style={{
                padding: '20px',
                background: currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.5)'
                  : currentThemeName === 'beige'
                  ? 'rgba(250, 235, 215, 0.5)'
                  : 'rgba(248, 250, 252, 0.8)',
                borderRadius: '12px',
                border: currentThemeName === 'dark'
                  ? '1px solid rgba(59, 130, 246, 0.15)'
                  : currentThemeName === 'beige'
                  ? '1px solid rgba(139, 69, 19, 0.1)'
                  : '1px solid rgba(59, 130, 246, 0.08)',
                textAlign: 'center'
              }}>
                <div style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: theme.primary || '#3b82f6',
                  marginBottom: '8px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  <Code size={24} style={{ display: 'inline-block' }} />
                </div>
                <div style={{
                  fontSize: '14px',
                  color: currentThemeName === 'dark' ? '#cbd5e1' : theme.textSecondary,
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                  {t('developer.fullStack')}
                </div>
              </div>
            </div>
          </div>

          {/* Current Work */}
          <div style={{
            marginBottom: '32px',
            padding: '24px',
            background: currentThemeName === 'dark'
              ? 'rgba(34, 197, 94, 0.1)'
              : currentThemeName === 'beige'
              ? 'rgba(139, 69, 19, 0.08)'
              : 'rgba(34, 197, 94, 0.05)',
            borderRadius: '16px',
            border: currentThemeName === 'dark'
              ? '1px solid rgba(34, 197, 94, 0.2)'
              : currentThemeName === 'beige'
              ? '1px solid rgba(139, 69, 19, 0.15)'
              : '1px solid rgba(34, 197, 94, 0.1)'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: theme.primaryText,
              marginBottom: '16px',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
              {t('developer.currentlyWorkingOn')}
            </h2>
            <ul style={{
              fontSize: '16px',
              lineHeight: '1.8',
              color: currentThemeName === 'dark' ? '#e2e8f0' : theme.text,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              paddingLeft: '24px',
              margin: 0,
              listStyleType: 'disc'
            }}>
              <li style={{ marginBottom: '12px' }}>{t('developer.workItem1')}</li>
              <li style={{ marginBottom: '12px' }}>{t('developer.workItem2')}</li>
              <li style={{ marginBottom: '0' }}>{t('developer.workItem3')}</li>
            </ul>
          </div>

          {/* Links */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <a
              href="https://bizbridge510.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '14px 24px',
                background: currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : currentThemeName === 'beige'
                  ? 'rgba(245, 245, 220, 0.9)'
                  : 'rgba(255, 255, 255, 0.95)',
                border: currentThemeName === 'dark'
                  ? '1px solid rgba(59, 130, 246, 0.2)'
                  : currentThemeName === 'beige'
                  ? '1px solid rgba(139, 69, 19, 0.15)'
                  : '1px solid rgba(59, 130, 246, 0.1)',
                borderRadius: '12px',
                color: theme.primaryText,
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '16px',
                fontWeight: '500',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 1)'
                  : currentThemeName === 'beige'
                  ? 'rgba(245, 245, 220, 1)'
                  : 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.borderColor = theme.primary || '#3b82f6';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : currentThemeName === 'beige'
                  ? 'rgba(245, 245, 220, 0.9)'
                  : 'rgba(255, 255, 255, 0.95)';
                e.currentTarget.style.borderColor = currentThemeName === 'dark'
                  ? 'rgba(59, 130, 246, 0.2)'
                  : currentThemeName === 'beige'
                  ? 'rgba(139, 69, 19, 0.15)'
                  : 'rgba(59, 130, 246, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Globe size={20} />
              {t('developer.visitWebsite')}
              <ExternalLink size={16} />
            </a>
            <a
              href="https://www.linkedin.com/in/ali-can-acar-25772b320/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '14px 24px',
                background: currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : currentThemeName === 'beige'
                  ? 'rgba(245, 245, 220, 0.9)'
                  : 'rgba(255, 255, 255, 0.95)',
                border: currentThemeName === 'dark'
                  ? '1px solid rgba(59, 130, 246, 0.2)'
                  : currentThemeName === 'beige'
                  ? '1px solid rgba(139, 69, 19, 0.15)'
                  : '1px solid rgba(59, 130, 246, 0.1)',
                borderRadius: '12px',
                color: theme.primaryText,
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '16px',
                fontWeight: '500',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 1)'
                  : currentThemeName === 'beige'
                  ? 'rgba(245, 245, 220, 1)'
                  : 'rgba(255, 255, 255, 1)';
                e.currentTarget.style.borderColor = theme.primary || '#3b82f6';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = currentThemeName === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : currentThemeName === 'beige'
                  ? 'rgba(245, 245, 220, 0.9)'
                  : 'rgba(255, 255, 255, 0.95)';
                e.currentTarget.style.borderColor = currentThemeName === 'dark'
                  ? 'rgba(59, 130, 246, 0.2)'
                  : currentThemeName === 'beige'
                  ? 'rgba(139, 69, 19, 0.15)'
                  : 'rgba(59, 130, 246, 0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Linkedin size={20} />
              {t('developer.linkedInProfile')}
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeveloperPage;

